import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import useScrollToTop from "../customHooks/useScrollToTop";
import RegisterService from "../../services/RegisterService";
import { useForm } from "react-hook-form";
import Loader from "../loader/Loader";
import SEO from "../../components/SEO";
import axios from "axios";
import { API_URL } from "../../constant/Constant";
import Multiselect from "multiselect-react-dropdown";
import toast from "react-hot-toast";
import Navbar from "../navbar/Navbar";

export default function AddAgent() {
  useScrollToTop();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState({
    managers: [],
    products: [],
  });
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productTouched, setProductTouched] = useState(false); // Track interaction
  const navigate = useNavigate();
  const managerMultiSelectRef = useRef(null);
  const productMultiSelectRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    trigger,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      manager: [],
      location: "",
      productAccess: [],
    },
  });

  useEffect(() => {
    // Fetch dropdown options from API
    const fetchDropdownOptions = async () => {
      try {
        const response = await axios.get(
          `${API_URL}api/v1/getDropdownOptionsForManagerAndAgents`
        );
        console.log(response.data);
        setDropdownOptions({
          managers: response.data.managers || [], // Assuming API returns 'managers'
          products: response.data.products || [],
        });
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
        setMessage("Failed to load dropdown options. Please try again later.");
      }
    };
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    setValue(
      "manager",
      selectedManagers.map((manager) => manager.id)
    );
  }, [selectedManagers, setValue]);

  useEffect(() => {
    const productIds = selectedProducts.map((product) => product.id);
    setValue("productAccess", productIds);
    // Only validate for UI if the field has been touched
    if (productTouched) {
      if (productIds.length > 0) {
        clearErrors("productAccess");
      } else {
        setError("productAccess", {
          type: "manual",
          message: "At least one product access option is required",
        });
      }
    }
  }, [selectedProducts, productTouched, setValue, setError, clearErrors]);

  const handleProductInteraction = (selectedList) => {
    setSelectedProducts(selectedList);
    if (!productTouched) {
      setProductTouched(true); // Mark as touched on first interaction
    }
  };

  const onSubmit = async (data) => {
    // Validate productAccess on every submit
    if (data.productAccess.length === 0) {
      setError("productAccess", {
        type: "manual",
        message: "At least one product access option is required",
      });
      setProductTouched(true); // Ensure error shows on UI
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setMessage("");
    try {
      const response = await RegisterService.registerAgent({
        emailId: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        managerIds: data.manager, // Changed from agentIds to managerIds
        location: data.location,
        productAccessOptionIds: data.productAccess,
        createdBy: localStorage.getItem("userId"),
      });
      setIsLoading(false);
      if (response.status === 200) {
        toast.success("Agent added successfully");
        navigate("/userList");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Registration error:", error);
      if (error.message === "Network Error") {
        setMessage("Network Error");
      } else {
        switch (error.response ? error.response.status : "") {
          case 401:
          case 403:
          case 404:
          case 406:
          case 412:
            setMessage(error.response.data.failedReason);
            break;
          case 500:
            setMessage("Server error occurred. Please try again later.");
            break;
          default:
            setMessage("Unknown error occurred. Please try again later.");
            break;
        }
      }
    }
  };

  const managerOptions = dropdownOptions.managers.map((manager) => ({
    name: manager.managerName, // Assuming API provides managerName
    id: manager.managerId, // Assuming API provides managerId
  }));

  const productOptions = dropdownOptions.products.map((product) => ({
    name: product.productAccessOptionName,
    id: product.productAccessOptionId,
  }));

  return (
    <>
      <Navbar />
      <div className="flex min-h-full flex-1 flex-col justify-start px-6 py-3 lg:px-8">
        <div className="mx-auto w-full max-w-sm md:max-w-md">
          <div className="flex flex-col justify-center items-center">
            <h2 className="text-center text-2xl font-semibold leading-9 tracking-tight primary-text">
              Add Agent
            </h2>
          </div>
        </div>
        <div className="mt-2 mx-auto w-full max-w-sm md:max-w-md">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex md:flex-row flex-col gap-3">
              <div className="md:basis-1/2 basis-1">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-normal leading-6"
                >
                  First Name*
                </label>
                <div className="mt-2">
                  <input
                    id="firstName"
                    {...register("firstName", {
                      required: "First Name is required",
                      maxLength: {
                        value: 15,
                        message:
                          "First Name cannot be longer than 15 characters",
                      },
                      pattern: {
                        value: /^\S*$/,
                        message: "First Name cannot contain spaces",
                      },
                      pattern: {
                        value: /^[A-Za-z]+$/,
                        message: "First Name can only contain letters",
                      },
                    })}
                    type="text"
                    autoComplete="first-name"
                    placeholder="Enter your first name"
                    className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-30 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 ${
                      errors.firstName && touchedFields.firstName
                        ? "border-red-500"
                        : ""
                    }`}
                    onBlur={() => trigger("firstName")}
                  />
                </div>
                {errors.firstName && (
                  <p className="error-text">{errors.firstName.message}</p>
                )}
              </div>
              <div className="md:basis-1/2 basis-1">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-normal leading-6"
                >
                  Last Name*
                </label>
                <div className="mt-2">
                  <input
                    id="lastName"
                    {...register("lastName", {
                      required: "Last Name is required",
                      maxLength: {
                        value: 30,
                        message:
                          "Last Name cannot be longer than 30 characters",
                      },
                      pattern: {
                        value: /^\S*$/,
                        message: "Last Name cannot contain spaces",
                      },
                      pattern: {
                        value: /^[A-Za-z]+$/,
                        message: "Last Name can only contain letters",
                      },
                    })}
                    type="text"
                    autoComplete="last-name"
                    placeholder="Enter your last name"
                    className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-30 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 ${
                      errors.lastName && touchedFields.lastName
                        ? "border-red-500"
                        : ""
                    }`}
                    onBlur={() => trigger("lastName")}
                  />
                </div>
                {errors.lastName && (
                  <p className="error-text">{errors.lastName.message}</p>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-normal leading-6"
              >
                Email address*
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  {...register("email", {
                    required: "Email is required",
                    maxLength: {
                      value: 60,
                      message: "Email cannot be longer than 60 characters",
                    },
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email format",
                    },
                  })}
                  type="email"
                  placeholder="Your e-mail address"
                  autoComplete="off"
                  className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-50 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 ${
                    errors.email && touchedFields.email ? "border-red-500" : ""
                  }`}
                  onBlur={() => trigger("email")}
                />
              </div>
              {errors.email && (
                <p className="error-text">{errors.email.message}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="location"
                className="block text-sm font-normal leading-6"
              >
                Location*
              </label>
              <div className="mt-2">
                <input
                  id="location"
                  {...register("location", {
                    required: "Location is required",
                    maxLength: {
                      value: 25,
                      message: "Location cannot be longer than 25 characters",
                    },
                    pattern: {
                      value: /^[A-Za-z0-9\s()\/\-\—]*$/,
                      message: "Location can only contain letters, digits, spaces, (), /, -, or —",
                    },
                    validate: (value) =>
                      value.trim() !== "" || "Location cannot be empty or only whitespace",
                  })}
                  type="text"
                  placeholder="Enter location"
                  className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-teal-800 placeholder-opacity-30 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 ${
                    errors.location && touchedFields.location
                      ? "border-red-500"
                      : ""
                    }`}
                  onBlur={() => trigger("location")}
                />
              </div>
              {errors.location && (
                <p className="error-text">{errors.location.message}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="manager"
                className="block text-sm font-normal leading-6"
              >
                Managers
              </label>
              <div className="mt-2">
                <input type="hidden" {...register("manager")} />
                <Multiselect
                  options={managerOptions}
                  displayValue="name"
                  selectedValues={selectedManagers}
                  onSelect={(selectedList) => setSelectedManagers(selectedList)}
                  onRemove={(selectedList) => setSelectedManagers(selectedList)}
                  className="block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-opacity-50 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6"
                  placeholder="Select managers"
                  ref={managerMultiSelectRef}
                />
                {errors.manager && (
                  <p className="error-text">{errors.manager.message}</p>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="productAccess"
                className="block text-sm font-normal leading-6"
              >
                Product Access*
              </label>
              <div className="mt-2">
                <input
                  type="hidden"
                  {...register("productAccess", {
                    validate: (value) =>
                      value.length > 0 ||
                      "At least one product access option is required",
                  })}
                />
                <Multiselect
                  options={productOptions}
                  displayValue="name"
                  selectedValues={selectedProducts}
                  onSelect={handleProductInteraction}
                  onRemove={handleProductInteraction}
                  onKeyPressFn={() => setProductTouched(true)}
                  onSearch={() => setProductTouched(true)}
                  className={`block w-full rounded-md border custom-border-50 focus:border-1 focus:bg-custom-blue placeholder-opacity-50 focus:ring-1 focus:ring-teal-600 px-3 py-1.5 bg-transparent sm:text-sm sm:leading-6 ${
                    errors.productAccess ? "border-red-500" : ""
                  }`}
                  placeholder="Select product access"
                  ref={productMultiSelectRef}
                />
                {errors.productAccess && (
                  <p className="error-text">{errors.productAccess.message}</p>
                )}
              </div>
            </div>
            {message && <p className="error-text">{message}</p>}
            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md cta-one px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
              >
                Submit
              </button>
              {isLoading && <p>Loading...</p>}
            </div>
          </form>
        </div>
        {isLoading && <Loader />}
      </div>
    </>
  );
}